import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';
import { withRouter } from 'react-router-dom';
import { HashLink as Link } from 'react-router-hash-link';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Progress from 'components/Progress';

import APIHandler from 'config/APIHandler';

import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

const Overlay = () => (
  <div className="overlay">
    <FontAwesomeIcon icon={['fas', 'lock']} />
  </div>
);

const TrainingCategoryCard = ({
  appId,
  category,
  redirPath = '/main/training/extra/',
  locked = false,
  comingSoon = false,
  foundationLocked = false,
}) => {
  const { lang } = useSettings();
  const { user: { email } } = useUserInfo();

  const { langFile } = lang;
  const locale = `${langFile.toLowerCase()}-${langFile
    .replace('pt', 'BR')
    .replace('es', '41')
    .replace('ko', 'kr')
    .toUpperCase()}`;

  const [loading, setLoading] = useState(true);
  const { badge, id, percentage, title } = category;
  const handleLoaded = useCallback(() => {
    setLoading(false);
  }, []);

  const buttonText = useMemo(() => {
    let txt = '';
    switch (percentage) {
      case 100:
        txt = i18n.translate('trainingScreens.restart');
        break;
      case 0:
        txt = i18n.translate('trainingScreens.start');
        break;
      default:
        txt = i18n.translate('trainingScreens.continue');
    }
    return txt;
  }, [percentage]);

  const linkUrl = useMemo(() => {
    const separator = redirPath.endsWith('/') ? '' : '/';
    if (percentage >= 100) {
      return `/main/retain/extra${separator}${id}`;
    }

    return `${redirPath}${separator}${id}`;
  }, [redirPath, id, percentage]);

  let translatedTitle = title;
  if (langFile !== 'en' && category?.translations?.length > 0) {
    translatedTitle = category.translations.find((t) => t.locale === locale)?.title || title;
  }

  const classes = clsx('trainingCard', 'badgeChapter', {
    disabled: locked || comingSoon,
    locked: locked || comingSoon,
  });

  return (
    <React.Fragment>
      {!window.location.pathname.includes('retain') && !loading && percentage === 100 && !locked && badge && (
        <div>
          <a href={`https://stack.travpromobile.com/download.php?url=${badge}`} alt={title} className="badge">
            <FontAwesomeIcon icon={['far', 'file-certificate']} />
            {i18n.translate('trainingScreens.badge')}
          </a>

          <FontAwesomeIcon icon={['far', 'check-circle']} className="chapterComplete" />
        </div>
      )}
      {!window.location.pathname.includes('retain') && !loading && percentage !== 100 && !locked && badge && (
        <div className="spacer-badge" />
      )}
      <Link
        to={linkUrl}
        className={classes}
        {...(foundationLocked
          ? {
            onClick: (e) => {
              e.preventDefault();
            },
          } : {}
        )}
        {...(percentage >= 100 
          ? {
            onClick: () => {
              APIHandler.trackEvent(appId, email, 'retain', 'section', id, title).then(() => {});
            }
          } : {}
        )}
      >
        <div className="textContainer textContainer__container--category">
          {!loading && locked && (
            <div>
              {comingSoon && (
                <h4 className="trainingCard__title">{i18n.translate('trainingScreens.comingSoon', { title })}</h4>
              )}
              {!comingSoon && (
                <h4 className="trainingCard__title">{i18n.translate('trainingScreens.unlockFoundation')}</h4>
              )}
            </div>
          )}
          {foundationLocked && <p>{i18n.translate('trainingScreens.unlockFoundation')}</p>}
        </div>
        <div className="imageContainer imageContainer__container--category">
          {/* FIXME: As with trainingcard, refactor Image component before reimplementation */}
          {/* <Image
          appId={appId}
          load={() => {
            setLoading(false);
          }}
          src={badge}
          alt={title}
        /> */}
          <img src={badge} alt={title} onLoad={handleLoaded} />

          <h4 className="trainingCard__title">{translatedTitle}</h4>
          {!loading && !foundationLocked && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {buttonText}
            </button>
          )}

          {!loading && locked && <Overlay />}
          {/* {!loading && <span className="chapterNumber">{`0${index + 1}`.slice(-2)}</span> && (
          <span className="chapterComplete">
            {percentage === 100 && !locked && (
              <FontAwesomeIcon icon={['far', 'check-circle']} className="chapterComplete" />
            )}
          </span>
        )} */}
        </div>
        {!loading && <Progress id={`ca${id}`} value={percentage} />}
      </Link>
    </React.Fragment>
  );
};

TrainingCategoryCard.propTypes = {
  appId: PropTypes.number.isRequired,
  category: PropTypes.shape({
    badge: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    percentage: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    translations: PropTypes.arrayOf(
      PropTypes.shape({
        locale: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  redirPath: PropTypes.string,
  locked: PropTypes.bool,
  comingSoon: PropTypes.bool,
  lockedSubtitle: PropTypes.bool,
  lockedSubtitleOverride: PropTypes.string,
  foundationLocked: PropTypes.bool,
};

export default withRouter(TrainingCategoryCard);
